import React, { useEffect, useState } from 'react';
import './InvoicesDetailsGrid.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import eFormType from '../../main/assets/enums/eFormType';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IInvoices from '../../main/interfaces/IInvoice';
import axiosInstance from '../../main/initializers/axios';
import EditModal from '../../main/components/modal/edit/EditModal';
import InvoiceInventoryComp from '../restaurant/invoiceSection/invoiceInventory/InvoiceInventoryComp';
import GeneralTable from '../../main/components/grid/GeneralTable';
import IPagination from '../../main/interfaces/IPagination';
import IGridItems from '../../main/interfaces/IGridItems';
import eInvoiceStatus from '../../main/assets/enums/eInvoiceStatus';
import { useTranslation } from 'react-i18next';

const AUDIT_STATUS_COLUMN = '__invoiceAuditStatus';
const CREATED_BY_COLUMN = '__invoiceCreatedBy';
const INVOICE_MOBILE_CARD_COLUMNS = [
  'Control',
  'Id',
  'TableName',
  'TableCode',
  'Created',
  'CreatedByName',
  'ClosedByUserFirstName',
  'CreatedByUserFirstName',
  'CratedByUserFirstName',
  'CurrentPrice',
  'Price',
];

const InvoicesDetailsGrid = () => {
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState<IInvoices[]>([]);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [aprovedUser, setAprovedUser] = useState<boolean>(false);
  const [selectedVal, setSelectedVal] = useState<IInvoices | null>(null);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [createdDate, setCreatedDate] = useState<string>('');

  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const getInvoiceId = (inv: any): number => Number(inv?.id ?? inv?.Id ?? inv?.invoiceId ?? inv?.InvoiceId ?? 0);
  const withoutSystemColumns = (cols: IGridItems[]) => (cols ?? []).filter((col) => ![AUDIT_STATUS_COLUMN, CREATED_BY_COLUMN].includes(col.entity));
  const withSystemColumns = (cols: IGridItems[]) => [
    {
      entity: AUDIT_STATUS_COLUMN,
      isShown: true,
      label: 'Control',
      sort: null,
      filter: null,
    },
    {
      entity: CREATED_BY_COLUMN,
      isShown: true,
      label: 'CreatedByName',
      sort: null,
      filter: null,
    },
    ...withoutSystemColumns(cols),
  ];

  const getTable = (pag?: IPagination, gridOverride?: IGridItems[], showDeletedOverride?: boolean, createdDateOverride?: string) => {
    const effective = pag ?? pagination;
    const effectiveGrid = withoutSystemColumns(gridOverride ?? gridItems ?? []);
    const effectiveShowDeleted = showDeletedOverride ?? showDeleted;
    const effectiveCreatedDate = createdDateOverride ?? createdDate;

    axiosInstance.post(ApiConstants.invoice.listCt, {
      gridItems: effectiveGrid,
      pagination: effective,
      showDeleted: effectiveShowDeleted,
      createdFrom: effectiveCreatedDate || null,
      createdTo: effectiveCreatedDate || null,
    }).then((res) => {
      const listTable = res.data?.items ?? [];
      const p = res.data?.pagination ?? {};
      const cols = res.data?.gridItems ?? [];

      setInvoices(listTable);
      setGridItems(withSystemColumns(cols));

      setPagination((prev) => ({
        ...prev,
        count: p.count ?? prev.count,
        page: p.page ?? prev.page,
        itemPerPage: p.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
      }));
    });
  };

  useEffect(() => {
    getTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGridItems((prev) => (prev.length ? withSystemColumns(prev) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    getTable(pag);
  };

  const handleShowDeletedChange = (checked: boolean) => {
    const nextPagination = { ...pagination, page: 1 };
    setShowDeleted(checked);
    setPagination(nextPagination);
    setSelectedVal(null);
    getTable(nextPagination, undefined, checked);
  };

  const handleCreatedDateChange = (value: string) => {
    const nextPagination = { ...pagination, page: 1 };
    setCreatedDate(value);
    setPagination(nextPagination);
    setSelectedVal(null);
    getTable(nextPagination, undefined, undefined, value);
  };

  const deleteDataSet = async (id: number) => {
    if (!id) return;

    try {
      await axiosInstance.delete(ApiConstants.invoice.delete, {
        data: { Id: id },
      });

      setIsModalDeleteOpen(false);
      setInvoices((prev: any[]) => prev.filter((x) => getInvoiceId(x) !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (item: IInvoices) => {
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IInvoices) => {
    if (isInvoiceDeleted(item)) {
      return;
    }

    if (item.status !== eInvoiceStatus.Open) {
      alert(t('errors.ClosedInvoiceCannotBeDeleted'));
      return;
    }

    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const isInvoiceDeleted = (invoice: IInvoices | null | undefined) => {
    const inv = invoice as any;
    return Boolean(inv?.deleted || inv?.Deleted || inv?.IsDelete || inv?.isDelete);
  };
  const hasDeletedRows = (invoice: IInvoices | null | undefined) => {
    const inv = invoice as any;
    return Boolean(inv?.hasDeletedRows || inv?.HasDeletedRows || inv?.invoiceRows?.some((row: any) => Boolean(row?.deleted || row?.Deleted)));
  };
  const getInvoiceAuditStatus = (invoice: IInvoices) => {
    const inv = invoice as any;
    if (isInvoiceDeleted(invoice)) return 'deleted';
    if (inv?.auditStatus === 'modified' || inv?.AuditStatus === 'modified' || hasDeletedRows(invoice)) return 'modified';
    return 'valid';
  };
  const normalizeStatusText = (value: unknown) =>
    String(value ?? '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');
  const isInvoiceDelivered = (invoice: IInvoices | null | undefined) => {
    const inv = invoice as any;
    if (!inv || isInvoiceDeleted(invoice)) return false;
    if (inv?.paid === true || inv?.Paid === true || inv?.isPaid === true || inv?.IsPaid === true) return true;

    const deliveredStatus = [
      inv?.submissionStatus,
      inv?.SubmissionStatus,
      inv?.submissionStatusDesc,
      inv?.SubmissionStatusDesc,
      inv?.deliveryStatus,
      inv?.DeliveryStatus,
      inv?.deliveryStatusDesc,
      inv?.DeliveryStatusDesc,
    ].some((value) => {
      const status = normalizeStatusText(value);
      return status === 'delivered' || status === 'submitted' || status === 'dorezuar' || status === 'dorzuar';
    });

    return deliveredStatus;
  };
  const getInvoiceRowClassName = (invoice: IInvoices) =>
    [
      'idgInvoiceRow',
      `idgInvoiceRow--${getInvoiceAuditStatus(invoice)}`,
      isInvoiceDelivered(invoice) ? 'idgInvoiceRow--delivered' : '',
    ]
      .filter(Boolean)
      .join(' ');
  const getInvoiceAuditLabel = (invoice: IInvoices) => {
    const status = getInvoiceAuditStatus(invoice);
    if (status === 'deleted') return t('invoice.auditDeleted', { defaultValue: 'Deleted' });
    if (status === 'modified') return t('invoice.auditModified', { defaultValue: 'Modified' });
    return t('invoice.auditValid', { defaultValue: 'Valid' });
  };
  const getCreatedByName = (invoice: IInvoices) => {
    const inv = invoice as any;
    const user = inv?.cratedByUser ?? inv?.CratedByUser ?? inv?.createdByUser ?? inv?.CreatedByUser;
    const fullName = [user?.firstName ?? user?.FirstName, user?.lastName ?? user?.LastName].filter(Boolean).join(' ').trim();

    return fullName || user?.userName || user?.UserName || inv?.userName || inv?.UserName || '-';
  };
  const renderInvoiceCell = (invoice: IInvoices, column: IGridItems) => {
    if (column.entity === CREATED_BY_COLUMN) {
      return getCreatedByName(invoice);
    }

    if (column.entity === AUDIT_STATUS_COLUMN) {
      const status = getInvoiceAuditStatus(invoice);
      return (
        <span className={`idgAuditBadge idgAuditBadge--${status}`}>
          <span className="idgAuditBadgeBox" />
          <span>{getInvoiceAuditLabel(invoice)}</span>
        </span>
      );
    }

    return undefined;
  };

  return (
    <BasicCli>
      <div className="idgWrap bcPageContent">
        <div className="idgContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{t('invoice.invoicesListings')}</h1>
          </div>

          <div className=" idgTable">
            <GeneralTable
              data={invoices}
              columns={gridItems}
              gridEntity={3}
              title=""
              hasRp={true}
              selectedItem={(val) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canEdit={(item) => !isInvoiceDeleted(item)}
              canDelete={(item) => !isInvoiceDeleted(item)}
              getRowClassName={getInvoiceRowClassName}
              renderCell={renderInvoiceCell}
              mobileCardColumns={INVOICE_MOBILE_CARD_COLUMNS}
              pagination={pagination}
              selectedItemPagination={(val) => selectedItemPagination(val)}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            >
              <div className="idgToolbarFilters">
                <label className="idgDeletedToggle">
                  <input type="checkbox" checked={showDeleted} onChange={(event) => handleShowDeletedChange(event.target.checked)} />
                  <span>{t('invoice.showDeletedInvoices', { defaultValue: 'Invoices with deleted rows' })}</span>
                </label>

                <label className="idgDateFilter" title={t('invoice.createdDate', { defaultValue: 'Date' })}>
                  <input
                    type="date"
                    value={createdDate}
                    onChange={(event) => handleCreatedDateChange(event.target.value)}
                  />
                </label>
              </div>
            </GeneralTable>
          </div>

          {isModalEditOpen ? (
            <EditModal
              title={t('invoice.editInvoice')}
              width="80%"
              hideApproveButton={true}
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setAprovedUser(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <InvoiceInventoryComp
                createOrUpdate={aprovedUser}
                tempInvoice={selectedVal}
                modalType={eFormType.Edit}
                onSubmitData={() => {
                  setAprovedUser(false);
                  getTable();
                }}
              />
            </EditModal>
          ) : null}

          {isModalDeleteOpen ? (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(getInvoiceId(selectedVal))}>
              {t('generic.wantToDelete')} <strong className="text-danger"> {getInvoiceId(selectedVal)}</strong>.
            </DeleteModal>
          ) : null}
        </div>
      </div>
    </BasicCli>
  );
};

export default InvoicesDetailsGrid;
